import { IconName } from '@aidkitorg/component-library/dist/types/src/Icon';
import type { ReportConfig } from '@aidkitorg/aidkit/lib/admin_panel/reports/domain/ReportConfig';
import type { Report } from '../Contexts/ReportsContext';

export type ReportPaletteCommand = {
  id: string;
  label: string;
  description?: string;
  href: string;
  iconName: IconName;
  category: 'reports';
  priority: number;
};

export type PaletteReport = Pick<Report, 'id' | 'title' | 'description'>;

/**
 * Shared with ReportsContext. Cache stores already-localized `Report[]`, so the
 * key must include `lang` — otherwise a language switch reuses stale titles.
 */
export function distroReportsQueryKey(lang: string) {
  return ['distro-reports', lang] as const;
}

export function mapReportsToPaletteCommands(reports: PaletteReport[]): ReportPaletteCommand[] {
  const commands: ReportPaletteCommand[] = [];

  for (const report of reports) {
    if (!report.id || !report.title) continue;

    commands.push({
      id: `report-${report.id}`,
      label: report.title,
      description: report.description || undefined,
      href: `/admin-panel/reports/${report.id}`,
      iconName: 'TableCellsIcon',
      category: 'reports',
      priority: 0,
    });
  }

  return commands;
}

function resolveLocalizedText(
  value: string | { en: string; [key: string]: string | undefined } | undefined,
  lang?: string
): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return (lang && value[lang]) || value.en || '';
}

type DistroReportListItem = {
  id?: string;
  name?: string | { en: string; [key: string]: string | undefined };
  description?: string | { en: string; [key: string]: string | undefined };
  author?: string;
  author_name?: string;
  updated_at?: number;
  locked?: boolean;
  groupBy?: ReportConfig['groupBy'];
  table?: {
    kind?: string;
    columns?: ReportConfig['columns'];
    filter?: ReportConfig['filter'];
    linksToSection?: ReportConfig['linksToSection'];
  };
};

/**
 * Distro list rows use `name` for the title.
 * Shape must match ReportsContext.transformDistroReport — both write
 * ['distro-reports', lang], and getReports reuses palette-populated entries.
 */
export function transformDistroReportForPalette(
  report: DistroReportListItem,
  lang?: string
): Report | null {
  if (!report.id) return null;
  const title = resolveLocalizedText(report.name, lang);
  if (!title) return null;
  return {
    id: report.id,
    title,
    description: resolveLocalizedText(report.description, lang),
    created_by: report.author || 'system',
    created_by_name: report.author_name || 'System',
    created_at: 0,
    updated_at: report.updated_at || 0,
    view_only: false,
    status: 'published',
    config: report.table ? {
      columns: report.table.columns || [],
      filter: report.table.filter,
      ...(report.groupBy && { groupBy: report.groupBy }),
      ...(report.table.linksToSection && { linksToSection: report.table.linksToSection }),
    } : undefined,
    _isCustomQuery: report.table?.kind === 'Custom Query',
    locked: report.locked,
  };
}

type AdminPanelReportsResponse = {
  data?: {
    reports?: {
      data?: Array<{
        id: string;
        title: string;
        description?: string;
        created_by: string;
        created_by_name: string;
        created_at: number;
        updated_at: number;
        view_only: boolean;
        status: 'draft' | 'published';
      }>;
    };
  };
};

export function transformDbReportsForPalette(dbResult: unknown): Report[] {
  if (!dbResult || typeof dbResult !== 'object' || !('data' in dbResult)) return [];
  const rows = (dbResult as AdminPanelReportsResponse).data?.reports?.data;
  if (!rows) return [];

  return rows.map((r) => ({
    id: r.id,
    title: r.title,
    description: r.description || '',
    created_by: r.created_by,
    created_by_name: r.created_by_name,
    created_at: r.created_at,
    updated_at: r.updated_at,
    view_only: r.view_only,
    status: r.status,
    _fromDb: true,
  }));
}

/** Merge DB then distro (distro wins on id collision), matching ReportsContext.getReports. */
export function mergeReportsForPalette(dbReports: Report[], distroReports: Report[]): Report[] {
  const reportsMap = new Map<string, Report>();
  for (const r of dbReports) {
    reportsMap.set(r.id, r);
  }
  for (const r of distroReports) {
    reportsMap.set(r.id, r);
  }
  return Array.from(reportsMap.values());
}

/** Distro list endpoint returns Report[]; usePost may return `{ error }` instead of throwing. */
export function isDistroReportsListResponse(result: unknown): result is DistroReportListItem[] {
  return Array.isArray(result);
}

/**
 * Admin panel reports list returns a payload with `data`. Error / catch sentinels
 * (`null`, `{ error }`, `{ success: false }`) must not be treated as success.
 */
export function isAdminPanelReportsResponse(result: unknown): boolean {
  return result != null && typeof result === 'object' && 'data' in result;
}

/**
 * Build the merged palette list from raw fetch results.
 * `cacheable` is true only when both sources succeeded — do not write `[]` from
 * failures into `['distro-reports', lang]` (empty arrays are truthy, so later
 * readers treat the cache as warm and never refetch).
 */
export function mergeFetchedReportsForPalette(
  distroResult: unknown,
  dbResult: unknown,
  lang?: string
): { reports: Report[]; cacheable: boolean } {
  const distroOk = isDistroReportsListResponse(distroResult);
  const dbOk = isAdminPanelReportsResponse(dbResult);

  const distroReports = distroOk
    ? distroResult
      .map((r) => transformDistroReportForPalette(r, lang))
      .filter((r): r is Report => r !== null)
    : [];
  const dbReports = transformDbReportsForPalette(dbOk ? dbResult : null);

  return {
    reports: mergeReportsForPalette(dbReports, distroReports),
    cacheable: distroOk && dbOk,
  };
}
